import { useState, type FormEvent } from "react";

const FORM_ACTION = "https://readdy.ai/api/form/db4nfv9rqll58fj4o9u0";

const feedbackTypes = ["内容纠错", "资源建议", "功能反馈", "合作咨询", "其他"];

type Status = "idle" | "submitting" | "success" | "error";

export default function FeedbackForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setFormError("");
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      payload.append(key, typeof value === "string" ? value : value.name);
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(FORM_ACTION, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        setFormError("");
        form.reset();
      } else {
        setStatus("error");
        setFormError(isSpam ? "提交被识别为异常请求，请稍后重试。" : serverMsg || "提交失败，请稍后重试。");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请稍后重试。");
    }
  };

  return (
    <form
      id="feedbackForm"
      data-readdy-form
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-200 bg-background-100 p-5 md:p-6"
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="fb-name" className="mb-1.5 block text-sm font-medium text-foreground-800">
            称呼
          </label>
          <input
            id="fb-name"
            name="name"
            type="text"
            required
            className="h-11 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
            placeholder="请输入你的称呼"
          />
        </div>
        <div>
          <label htmlFor="fb-email" className="mb-1.5 block text-sm font-medium text-foreground-800">
            邮箱
          </label>
          <input
            id="fb-email"
            name="email"
            type="email"
            required
            className="h-11 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
            placeholder="用于接收回复的邮箱"
          />
        </div>
      </div>

      <div className="mt-4">
        <label htmlFor="fb-type" className="mb-1.5 block text-sm font-medium text-foreground-800">
          反馈类型
        </label>
        <select
          id="fb-type"
          name="type"
          required
          defaultValue=""
          className="h-11 w-full cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none"
        >
          <option value="" disabled>
            请选择反馈类型
          </option>
          {feedbackTypes.map((type) => (
            <option key={type} value={type}>
              {type}
            </option>
          ))}
        </select>
      </div>

      <div className="mt-4">
        <label htmlFor="fb-message" className="mb-1.5 block text-sm font-medium text-foreground-800">
          反馈内容
        </label>
        <textarea
          id="fb-message"
          name="message"
          required
          maxLength={500}
          rows={5}
          className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
          placeholder="请描述你遇到的问题或建议，最多 500 字"
        />
      </div>

      <input
        className="form-extra-note"
        type="text"
        name="website_alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
      />

      <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center">
        <button
          type="submit"
          disabled={status === "submitting"}
          className="flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <i className="ri-send-plane-line" />
          {status === "submitting" ? "提交中…" : "提交反馈"}
        </button>
        {status === "success" && (
          <span className="flex items-center gap-1.5 text-sm font-medium text-primary-600">
            <i className="ri-checkbox-circle-line text-base" />
            提交成功，感谢你的反馈！
          </span>
        )}
        {status === "error" && (
          <span className="flex items-center gap-1.5 text-sm font-medium text-accent-700">
            <i className="ri-error-warning-line text-base" />
            {formError || "提交失败，请稍后重试。"}
          </span>
        )}
      </div>
    </form>
  );
}